import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection, doc, getDoc, onSnapshot, query, where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../auth/AuthProvider";
import { friendly } from "../lib/errors";
import type { MatchDoc } from "../lib/types";
import Countdown from "../components/Countdown";

interface Row {
  id: string;
  otherUid: string;
  match: MatchDoc;
}

// Small in-memory profile cache (name + first photo) shared across renders.
const profileCache = new Map<string, { name: string; photo: string | null }>();

function useOtherProfile(uid: string) {
  const [p, setP] = useState(profileCache.get(uid) ?? null);
  useEffect(() => {
    if (profileCache.has(uid)) return;
    let alive = true;
    void getDoc(doc(db, "users", uid))
      .then((s) => {
        const d = s.data();
        const v = {
          name: (d?.displayName as string) ?? "Someone",
          photo: (d?.photoURLs as string[] | undefined)?.[0] ?? null,
        };
        profileCache.set(uid, v);
        if (alive) setP(v);
      })
      .catch(() => {
        // Deleted account or blocked — show a placeholder, don't crash the list.
        const v = { name: "Someone", photo: null };
        profileCache.set(uid, v);
        if (alive) setP(v);
      });
    return () => { alive = false; };
  }, [uid]);
  return p;
}

export default function Matches() {
  const { uid } = useAuth();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) return;
    // No orderBy: array-contains + orderBy needs a composite index, and we
    // sort client-side by latest activity anyway.
    const q = query(
      collection(db, "matches"),
      where("participants", "array-contains", uid),
    );
    return onSnapshot(
      q,
      (snap) => {
        const rs = snap.docs
          .map((d) => {
            const m = d.data() as MatchDoc;
            return { id: d.id, match: m, otherUid: m.participants.find((p) => p !== uid) ?? "" };
          })
          .filter((r) => r.otherUid && r.match.state !== "expired")
          // Most recent activity first (createdAt fallback).
          .sort((a, b) =>
            (b.match.lastMessageAt?.toMillis() ?? b.match.createdAt?.toMillis() ?? 0) -
            (a.match.lastMessageAt?.toMillis() ?? a.match.createdAt?.toMillis() ?? 0));
        setRows(rs);
        setError(null);
      },
      (e) => setError(friendly(e)),
    );
  }, [uid]);

  if (error) {
    return (
      <div className="screen">
        <h1>Matches</h1>
        <div className="error">{error}</div>
      </div>
    );
  }
  if (rows === null) return <div className="splash"><div className="spinner" /></div>;

  if (rows.length === 0) {
    // Empty state aligned with Discover's: content anchored above the tab bar,
    // pill bottom at the same height as "Jump into a Relay".
    return (
      <div className="screen deck-screen">
        <h1>Matches</h1>
        <p className="muted">Every match gets 10 messages. Make them count.</p>
        <div style={{ flex: 1 }} />
        <div className="center" style={{ transform: "translateY(-20px)" }}>
          <div style={{ fontSize: 46, marginBottom: 10 }} aria-hidden>✦</div>
          <p className="muted" style={{ margin: 0 }}>
            No matches yet. Like people in Discover — when it's mutual, they show up here.
          </p>
          <Link to="/discover" className="btn btn-inline" style={{ marginTop: 50, textDecoration: "none" }}>
            Go to Discover
          </Link>
        </div>
        <div style={{ flex: 1 }} />
      </div>
    );
  }

  return (
    <div className="screen">
      <h1>Matches</h1>
      <p className="muted">Every match gets 10 messages. Make them count.</p>
      <div className="match-list">
        {rows.map((r) => <MatchRow key={r.id} row={r} myUid={uid!} />)}
      </div>
    </div>
  );
}

function MatchRow({ row, myUid }: { row: Row; myUid: string }) {
  const other = useOtherProfile(row.otherUid);
  const m = row.match;

  const { unread, subtitle } = useMemo(() => {
    const lastMsg = m.lastMessageAt?.toMillis() ?? 0;
    const lastRead = m.lastRead?.[myUid]?.toMillis() ?? 0;
    const theyLast = m.streak.uid && m.streak.uid !== myUid;
    const unread = lastMsg > 0 && theyLast && lastMsg > lastRead;
    const remaining = Math.max(0, m.allotmentPerUser - (m.used[myUid] ?? 0));
    let subtitle: string;
    if (m.state === "awaitingPayment") subtitle = "Out of messages — decide to keep going";
    else if (lastMsg === 0) subtitle = "New match — say hi";
    else subtitle = `${remaining} message${remaining === 1 ? "" : "s"} left`;
    return { unread, subtitle };
  }, [m, myUid]);

  const deadline = m.state === "awaitingPayment" ? m.paywall?.deadline?.toMillis() ?? null : null;
  const urgent = deadline !== null && deadline - Date.now() < 6 * 3600_000;

  return (
    <Link to={`/chat/${row.id}`} className={"match-row" + (unread ? " match-row-unread" : "")}>
      {other?.photo ? (
        <img className="match-avatar" src={other.photo} alt="" />
      ) : (
        <div className="match-avatar match-avatar-empty">☺</div>
      )}
      <div className="match-meta">
        <div className="match-name">
          {other?.name ?? "…"}
          {unread && <span aria-label="unread message" title="Unread message">❤️</span>}
          {deadline !== null && (
            <span className="pill" style={{ color: urgent ? "#ff453a" : "#ffb020", borderColor: "currentColor" }}>
              ⏳ <Countdown endsAt={deadline} />
            </span>
          )}
        </div>
        <div className={"match-sub" + (unread ? " match-sub-unread" : "")}>{subtitle}</div>
      </div>
    </Link>
  );
}
